import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, CreditCard, LogOut, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModals } from '../contexts/AuthModalsContext';
import { AuthFlowButton } from './AuthFlowButton';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../theme/netflixTheme';

export function AppHeader(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isLoading } = useAuth();
  const { openAuthSelectionModal } = useAuthModals();
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success('Saiu com sucesso!');
    navigate('/');
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {showProfileMenu && (
        <div
          style={overlayStyle}
          onClick={() => setShowProfileMenu(false)}
        />
      )}
      <View style={styles.header}>
        <div style={headerContentStyle}>
        {/* Logo */}
        <Pressable onPress={() => navigate('/')} style={styles.logoSection}>
          <img
            src="/logo_light.png"
            alt="TvDorama"
            // eslint-disable-next-line react-native/no-inline-styles
            style={{
              height: 50,
              maxWidth: 200,
              objectFit: 'contain',
              cursor: 'pointer',
            }}
          />
        </Pressable>

        {/* Navigation */}
        <View style={styles.navSection}>
          <Pressable onPress={() => navigate('/')}>
            <Text style={[styles.navItem, isActive('/') && styles.navItemActive]}>
              Início
            </Text>
          </Pressable>
          <Pressable onPress={() => navigate('/search')}>
            <Text style={[styles.navItem, isActive('/search') && styles.navItemActive]}>
              Explorar
            </Text>
          </Pressable>
          <Pressable onPress={() => navigate('/watchlist')}>
            <Text style={[styles.navItem, isActive('/watchlist') && styles.navItemActive]}>
              Favoritos
            </Text>
          </Pressable>
          <Pressable onPress={() => navigate('/plans')}>
            <Text style={[styles.navItem, isActive('/plans') && styles.navItemActive]}>
              Planos
            </Text>
          </Pressable>
        </View>

        {/* Actions */}
        <View style={styles.actionsSection}>
          {!isLoading && user && (
            <View style={styles.profileMenu}>
              <Pressable
                onPress={() => setShowProfileMenu(!showProfileMenu)}
                style={styles.profileButtonWrapper}
              >
                <View style={styles.profileButtonContent}>
                  <View style={styles.avatar}>
                    <Text style={styles.avatarText}>
                      {user?.full_name?.charAt(0).toUpperCase() || 'U'}
                    </Text>
                  </View>
                  <Text style={styles.userName} numberOfLines={1}>
                    {user?.full_name?.split(' ')[0] || 'Usuário'}
                  </Text>
                </View>
              </Pressable>

              {showProfileMenu && (
                <View style={styles.dropdown}>
                  <View style={styles.dropdownHeader}>
                    <Text style={styles.dropdownHeaderText}>{user?.full_name}</Text>
                    <Text style={styles.dropdownHeaderEmail}>{user?.email}</Text>
                  </View>
                  <View style={styles.divider} />
                  <Pressable onPress={() => { navigate('/profile'); setShowProfileMenu(false); }} style={styles.dropdownItemButton}>
                    <User size={18} color={NETFLIX_COLORS.textPrimary} />
                    <Text style={styles.dropdownItem}>Meu Perfil</Text>
                  </Pressable>
                  <Pressable onPress={() => { navigate('/plans'); setShowProfileMenu(false); }} style={styles.dropdownItemButton}>
                    <CreditCard size={18} color={NETFLIX_COLORS.textPrimary} />
                    <Text style={styles.dropdownItem}>Meus Planos</Text>
                  </Pressable>
                  {user?.roles?.some((r: any) => r.name === 'Administrador') && (
                    <>
                      <View style={styles.divider} />
                      <Pressable onPress={() => { navigate('/admin'); setShowProfileMenu(false); }} style={styles.dropdownItemButton}>
                        <Shield size={18} color={NETFLIX_COLORS.primary} />
                        <Text style={[styles.dropdownItem, styles.adminItem]}>Painel Admin</Text>
                      </Pressable>
                    </>
                  )}
                  <View style={styles.divider} />
                  <Pressable onPress={handleLogout} style={styles.dropdownItemButton}>
                    <LogOut size={18} color={NETFLIX_COLORS.primary} />
                    <Text style={[styles.dropdownItem, styles.logoutItem]}>Sair</Text>
                  </Pressable>
                </View>
              )}
            </View>
          )}

          {!isLoading && !user && (
            <AuthFlowButton
              onSignupFirst={openAuthSelectionModal}
              onPaymentFirst={openAuthSelectionModal}
            />
          )}
        </View>
      </div>
    </View>
    </>
  );
}

const headerContentStyle: React.CSSProperties = {
  maxWidth: '1440px',
  margin: '0 auto',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `0 ${NETFLIX_SPACING.xl}px`,
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 99,
};

const styles = StyleSheet.create({
  header: {
    backgroundColor: NETFLIX_COLORS.background,
    paddingVertical: NETFLIX_SPACING.lg,
    borderBottomWidth: 0,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    overflow: 'visible',
    zIndex: 100,
  },
  logoSection: {
    minWidth: 200,
    marginRight: NETFLIX_SPACING.xxxl,
  },
  navSection: {
    flexDirection: 'row',
    gap: NETFLIX_SPACING.xxl,
    flex: 1,
    marginLeft: 0,
  },
  navItem: {
    color: NETFLIX_COLORS.textSecondary,
    fontSize: 15,
    fontWeight: '600',
    paddingVertical: NETFLIX_SPACING.sm,
    paddingHorizontal: NETFLIX_SPACING.sm,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  navItemActive: {
    color: NETFLIX_COLORS.primary,
    fontWeight: '700',
    borderBottomColor: NETFLIX_COLORS.primary,
  },
  actionsSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: NETFLIX_SPACING.xl,
  },
  profileMenu: {
    position: 'relative',
  },
  profileButtonWrapper: {
    padding: 0,
  },
  profileButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: NETFLIX_SPACING.md,
  },
  profileButton: {
    padding: 0,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: NETFLIX_COLORS.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    color: NETFLIX_COLORS.white,
    fontSize: 14,
    fontWeight: '700',
  },
  userName: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 13,
    fontWeight: '600',
    maxWidth: 120,
  },
  dropdown: {
    position: 'absolute',
    top: 55,
    right: 0,
    backgroundColor: NETFLIX_COLORS.surface,
    borderRadius: 8,
    minWidth: 240,
    maxWidth: 300,
    borderWidth: 1,
    borderColor: NETFLIX_COLORS.border,
    overflow: 'hidden',
    maxHeight: 400,
    zIndex: 1000,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
  },
  dropdownHeader: {
    paddingHorizontal: NETFLIX_SPACING.lg,
    paddingVertical: NETFLIX_SPACING.lg,
    backgroundColor: NETFLIX_COLORS.background,
  },
  dropdownHeaderText: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 14,
    fontWeight: '700',
    marginBottom: NETFLIX_SPACING.xs,
  },
  dropdownHeaderEmail: {
    color: NETFLIX_COLORS.textSecondary,
    fontSize: 12,
  },
  dropdownItemButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: NETFLIX_SPACING.md,
    paddingHorizontal: NETFLIX_SPACING.lg,
    paddingVertical: NETFLIX_SPACING.md,
  },
  dropdownItem: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 13,
  },
  logoutItem: {
    color: NETFLIX_COLORS.primary,
    fontWeight: '600',
  },
  adminItem: {
    color: NETFLIX_COLORS.primary,
    fontWeight: '600',
  },
  divider: {
    height: 1,
    backgroundColor: NETFLIX_COLORS.border,
  },
  loginBtn: {
    backgroundColor: NETFLIX_COLORS.primary,
    paddingHorizontal: NETFLIX_SPACING.xl,
    paddingVertical: NETFLIX_SPACING.md,
    borderRadius: 6,
  },
  loginBtnText: {
    color: NETFLIX_COLORS.white,
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
});
